import { useStudyingDeckStudyCardsQueryOptions } from "@-/dev.improve.haniverse.lib.cards.read";
import { usePrefetchedSuspenseQuery } from "@-/com.npmjs.tanstack__react-query";
import { DeckLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.deck._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMemo } from "react";
import { useLocalDayKey } from "@-/dev.improve.haniverse.lib.time.local-day";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import {
  createDeckCoreSliceLookup,
  compareDeckStudyCards,
  useFilteredDeckStudyCards,
} from "@-/dev.improve.haniverse.routes.nav.app.deck._lib.study-card-core";
import {
  buildDeckUpcomingSchedule,
  shiftDeckUpcomingRange,
  type DeckUpcomingSelection,
  type DeckUpcomingTimeframe,
} from "#build-deck-upcoming-schedule";

/** Counts for the visible window or an explicit selection, which may be outside the window. */
export function useDeckUpcomingPageModelSuspense({
  periodCount,
  rangeStartTime,
  selection,
  timeframe = "week",
}: {
  /** Bars in a moved window; a panned month view keeps its length instead of a calendar month. */
  periodCount?: number | null;
  rangeStartTime?: number | null;
  selection?: DeckUpcomingSelection;
  timeframe?: DeckUpcomingTimeframe;
} = {}) {
  const { data: deckStudyCards } = usePrefetchedSuspenseQuery(
    useStudyingDeckStudyCardsQueryOptions(),
  );
  const [sortMode] = useContextSelectors(DeckLayoutContext, (c) => c.sortMode);
  const { stageByFront, studyCardList } = useFilteredDeckStudyCards({ deckStudyCards });
  const todayKey = useLocalDayKey();
  // Include every row so meaning tiles retain readings hidden by the pinyin-flashcards setting.
  const coreSliceByCharacter = useMemo(
    () => createDeckCoreSliceLookup(deckStudyCards),
    [deckStudyCards],
  );
  // Quiz cards have no deck row, so the filtered list never holds them; with "Show quiz cards in
  // stats" on they join the bars as counts (see `buildDeckUpcomingSchedule`).
  const { isQuizCardsInStatsEnabled } = useActorUser();
  const sortedStudyCards = useMemo(
    () => [
      ...[...studyCardList].sort((a, b) => compareDeckStudyCards(sortMode, a, b)),
      ...(isQuizCardsInStatsEnabled
        ? deckStudyCards.filter((studyCard) => studyCard.userQuizCard != null)
        : []),
    ],
    [deckStudyCards, isQuizCardsInStatsEnabled, studyCardList, sortMode],
  );
  const schedule = useMemo(
    () =>
      buildDeckUpcomingSchedule(sortedStudyCards, new Date(`${todayKey}T00:00:00`), {
        includeQuizCards: isQuizCardsInStatsEnabled,
        periodCount: rangeStartTime == null ? undefined : (periodCount ?? undefined),
        rangeStart: rangeStartTime == null ? undefined : new Date(rangeStartTime),
        timeframe,
      }),
    [sortedStudyCards, todayKey, periodCount, rangeStartTime, timeframe, isQuizCardsInStatsEnabled],
  );
  // Keep an explicit selection's arrays and maps stable while only the chart window moves.
  const selectedPeriod = useMemo(
    () =>
      selection === undefined
        ? undefined
        : buildDeckUpcomingSchedule(sortedStudyCards, new Date(`${todayKey}T00:00:00`), {
            includeQuizCards: isQuizCardsInStatsEnabled,
            selection,
          }).selected,
    [sortedStudyCards, todayKey, selection, isQuizCardsInStatsEnabled],
  );
  // One window on either side supplies real bars while a pan moves between snapped windows.
  const adjacentPeriods = useMemo(() => {
    const before = buildDeckUpcomingSchedule(sortedStudyCards, new Date(`${todayKey}T00:00:00`), {
      includeQuizCards: isQuizCardsInStatsEnabled,
      periodCount: schedule.periods.length,
      rangeStart: shiftDeckUpcomingRange(schedule.rangeStart, timeframe, -schedule.periods.length),
      timeframe,
    }).periods;
    const after = buildDeckUpcomingSchedule(sortedStudyCards, new Date(`${todayKey}T00:00:00`), {
      includeQuizCards: isQuizCardsInStatsEnabled,
      periodCount: schedule.periods.length,
      rangeStart: schedule.rangeEnd,
      timeframe,
    }).periods;
    return { after, before };
  }, [
    isQuizCardsInStatsEnabled,
    sortedStudyCards,
    todayKey,
    schedule.rangeStart,
    schedule.rangeEnd,
    schedule.periods.length,
    timeframe,
  ]);
  return {
    ...schedule,
    adjacentPeriods,
    coreSliceByCharacter,
    selected: selectedPeriod ?? schedule.selected,
    stageByCharacter: stageByFront,
    todayKey,
  };
}

export type DeckUpcomingPageModel = ReturnType<typeof useDeckUpcomingPageModelSuspense>;
